import { useEffect, useState } from 'react'
import { client } from '../lib/api'
import {
  AUTO_REJECT,
  AUTO_REVIEW,
  ALLOWED_CONTENT,
  REMOVE_CATEGORIES,
  MODERATION_POLICY_VERSION,
} from '../../shared/moderation-policy'

type Tab = 'overview' | 'queue' | 'reports' | 'appeals' | 'users' | 'support' | 'audit' | 'policy'

export function Admin({ notify }: { notify: (msg: string) => void }) {
  const [tab, setTab] = useState<Tab>('overview')
  const [overview, setOverview] = useState<{ counts?: Record<string, number> } | null>(null)
  const [queue, setQueue] = useState<Record<string, unknown>[]>([])
  const [reports, setReports] = useState<Record<string, unknown>[]>([])
  const [appeals, setAppeals] = useState<Record<string, unknown>[]>([])
  const [users, setUsers] = useState<Record<string, unknown>[]>([])
  const [tickets, setTickets] = useState<Record<string, unknown>[]>([])
  const [logs, setLogs] = useState<Record<string, unknown>[]>([])
  const [notes, setNotes] = useState<Record<string, string>>({})

  const load = async (t: Tab) => {
    try {
      if (t === 'overview') setOverview((await client.adminOverview()) as { counts?: Record<string, number> })
      if (t === 'queue') setQueue((await client.adminModeration()).cases)
      if (t === 'reports') setReports((await client.adminReports()).reports)
      if (t === 'appeals') setAppeals((await client.adminAppeals()).appeals)
      if (t === 'users') setUsers((await client.adminUsers()).users)
      if (t === 'support') setTickets((await client.adminSupport()).tickets)
      if (t === 'audit') setLogs((await client.adminAudit()).logs)
    } catch (e) {
      notify((e as Error).message)
    }
  }

  useEffect(() => {
    void load(tab)
  }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (id: string, status: string) => {
    const reason = (notes[id] ?? '').trim()
    if (status === 'approved' && reason.length < 8) {
      notify('Approval requires a written reason (at least 8 characters).')
      return
    }
    try {
      await client.adminModerationDecide(id, { status, notes: reason })
      notify(`Case ${status}`)
      await load('queue')
    } catch (e) {
      notify((e as Error).message)
    }
  }

  const formatScore = (n: unknown) => {
    const v = typeof n === 'number' ? n : Number(n)
    if (!Number.isFinite(v)) return '—'
    return v.toFixed(2)
  }

  return (
    <div className="panel-page admin-layout">
      <span className="eyebrow">Moderation</span>
      <h1>Admin console</h1>

      <div className="admin-tabs">
        {(['overview', 'queue', 'reports', 'appeals', 'users', 'support', 'audit', 'policy'] as Tab[]).map((t) => (
          <button key={t} type="button" className={tab === t ? 'selected' : ''} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'overview' && overview && (
        <div className="admin-stats">
          {Object.entries(overview.counts ?? {}).map(([key, value]) => (
            <div key={key} className="stat-card">
              <div className="stat-label">{key.replace(/_/g, ' ')}</div>
              <div className="stat-value">{value}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'queue' && (
        <ul className="admin-list">
          {queue.map((c) => {
            const id = String(c.id)
            const targetType = String(c.target_type)
            const targetId = String(c.target_id)
            const signals = Array.isArray(c.signals) ? (c.signals as string[]) : []
            const scan = (c.scan as Record<string, unknown> | null) ?? null
            const scores = (scan?.scores as Record<string, unknown> | undefined) ?? {}
            const thresholds = (scan?.thresholds as Record<string, unknown> | undefined) ?? {}
            const labels = (scan?.labels as Record<string, unknown> | undefined) ?? {}
            return (
              <li key={id} className="admin-case">
                <div className="admin-case-head">
                  <strong>{targetType}</strong>
                  <span className="ticket-kind">{String(c.reason ?? '')}</span>
                  <span>P{String(c.priority ?? 0)}</span>
                  {c.auto_decision != null && <span className="ticket-kind">auto:{String(c.auto_decision)}</span>}
                </div>
                <p className="admin-case-meta">
                  {String(c.display_name ?? 'Unknown')} · {String(c.email ?? '—')}
                  {c.subject_user_id ? ` · ${String(c.subject_user_id).slice(0, 12)}…` : ''}
                </p>
                <p>{String(c.notes ?? '')}</p>
                {signals.length > 0 && (
                  <div className="admin-signal-row">
                    {signals.map((s) => (
                      <span key={s} className="admin-signal-chip">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
                {scan && (
                  <div className="admin-scan-panel">
                    <div className="admin-scan-grid">
                      <div>
                        <span className="stat-label">Provider</span>
                        <strong>{String(c.scanProvider ?? scan.provider ?? '—')}</strong>
                      </div>
                      <div>
                        <span className="stat-label">Faces</span>
                        <strong>{String(scan.faceCount ?? '—')}</strong>
                      </div>
                      <div>
                        <span className="stat-label">Est. age</span>
                        <strong>{String(scan.estimatedAge ?? '—')}</strong>
                      </div>
                      <div>
                        <span className="stat-label">Claimed age</span>
                        <strong>{String(scan.claimedAge ?? '—')}</strong>
                      </div>
                      <div>
                        <span className="stat-label">Deepfake</span>
                        <strong>
                          {formatScore(scores.deepfake)} / thr dual {formatScore((thresholds.deepfakeHighDual as { deepfake?: number })?.deepfake)}
                        </strong>
                      </div>
                      <div>
                        <span className="stat-label">GenAI</span>
                        <strong>
                          {formatScore(scores.genai)} / thr {formatScore((thresholds.deepfakeHighDual as { genai?: number })?.genai)}
                        </strong>
                      </div>
                      <div>
                        <span className="stat-label">Deepfake band</span>
                        <strong>{String(labels.deepfake ?? '—')}</strong>
                      </div>
                      <div>
                        <span className="stat-label">Violence</span>
                        <strong>
                          {String(labels.violence ?? '—')} (score {formatScore(scores.violence)})
                        </strong>
                      </div>
                      <div>
                        <span className="stat-label">Gore</span>
                        <strong>{formatScore(scores.goreProb)}</strong>
                      </div>
                      <div>
                        <span className="stat-label">Nudity</span>
                        <strong>
                          {String(labels.nudity ?? '—')} (erotica {formatScore(scores.erotica)})
                        </strong>
                      </div>
                      <div>
                        <span className="stat-label">Self-harm</span>
                        <strong>
                          {String(labels.selfHarm ?? '—')} ({formatScore(scores.selfHarm)})
                        </strong>
                      </div>
                      <div>
                        <span className="stat-label">SHA256</span>
                        <strong className="admin-mono">{String(c.photoSha256 ?? '—').slice(0, 16)}…</strong>
                      </div>
                    </div>
                    <p className="field-hint">
                      Review signal for deepfake: {String(thresholds.deepfakeReviewSignal ?? 'high only')}. Medium vendor
                      scores are ignored for real-face false positives.
                    </p>
                  </div>
                )}
                {targetType === 'photo' && (
                  <img className="admin-thumb" src={client.adminMediaUrl(targetId)} alt="" />
                )}
                <textarea
                  className="input"
                  rows={2}
                  placeholder="Required to approve — explain why (min 8 chars)"
                  value={notes[id] ?? ''}
                  onChange={(e) => setNotes((n) => ({ ...n, [id]: e.target.value }))}
                />
                <div className="modal-actions">
                  <button className="button primary" onClick={() => void decide(id, 'approved')}>
                    Approve
                  </button>
                  <button className="button secondary" onClick={() => void decide(id, 'held')}>
                    Hold
                  </button>
                  <button className="button secondary" onClick={() => void decide(id, 'rejected')}>
                    Reject
                  </button>
                  <button className="button secondary" onClick={() => void decide(id, 'removed')}>
                    Remove
                  </button>
                </div>
              </li>
            )
          })}
          {queue.length === 0 && <p className="page-sub">Queue empty.</p>}
        </ul>
      )}

      {tab === 'reports' && (
        <ul className="admin-list">
          {reports.map((r) => (
            <li key={String(r.id)} className="admin-case">
              <strong>{String(r.reason)}</strong>
              <p>{String(r.details ?? '')}</p>
              <small>
                {String(r.target_type)} · {String(r.status)}
              </small>
              <div className="modal-actions">
                <button
                  className="button primary"
                  onClick={() =>
                    void client.adminResolveReport(String(r.id), 'resolved').then(() => load('reports'))
                  }
                >
                  Resolve
                </button>
                <button
                  className="button secondary"
                  onClick={() =>
                    void client.adminResolveReport(String(r.id), 'dismissed').then(() => load('reports'))
                  }
                >
                  Dismiss
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {tab === 'appeals' && (
        <ul className="admin-list">
          {appeals.map((a) => (
            <li key={String(a.id)} className="admin-case">
              <p>{String(a.body)}</p>
              <small>{String(a.status)}</small>
              <div className="modal-actions">
                <button
                  className="button primary"
                  onClick={() =>
                    void client
                      .adminAppealDecide(String(a.id), { status: 'granted', response: 'Appeal granted.' })
                      .then(() => load('appeals'))
                  }
                >
                  Grant
                </button>
                <button
                  className="button secondary"
                  onClick={() =>
                    void client
                      .adminAppealDecide(String(a.id), { status: 'denied', response: 'Appeal denied.' })
                      .then(() => load('appeals'))
                  }
                >
                  Deny
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {tab === 'users' && (
        <ul className="admin-list">
          {users.map((u) => (
            <li key={String(u.id)} className="admin-case">
              <strong>{String(u.display_name ?? u.email)}</strong>
              <small>
                {String(u.email)} · {String(u.status)}
                {Number(u.is_admin) === 1 ? ' · admin' : ''}
              </small>
              <div className="modal-actions">
                {Number(u.is_admin) === 1 ? (
                  <button
                    className="button secondary"
                    onClick={() =>
                      void client
                        .adminDemoteUser(String(u.id))
                        .then((r) => {
                          notify(r.message ?? 'Demoted')
                          void load('users')
                        })
                        .catch((e) => notify((e as Error).message))
                    }
                  >
                    Remove admin
                  </button>
                ) : (
                  <button
                    className="button primary"
                    onClick={() =>
                      void client
                        .adminPromoteUser(String(u.id))
                        .then((r) => {
                          notify(r.message ?? 'Promoted')
                          void load('users')
                        })
                        .catch((e) => notify((e as Error).message))
                    }
                  >
                    Make admin
                  </button>
                )}
                <button
                  className="button secondary"
                  onClick={() =>
                    void client
                      .adminSuspendUser(String(u.id), { reason: 'Moderator suspension' })
                      .then(() => notify('Suspended'))
                  }
                >
                  Suspend
                </button>
                <button
                  className="button secondary"
                  onClick={() =>
                    void client
                      .adminSuspendUser(String(u.id), { reason: 'Permanent ban', permanent: true })
                      .then(() => notify('Banned'))
                  }
                >
                  Ban
                </button>
                <button
                  className="button primary"
                  onClick={() => void client.adminReinstateUser(String(u.id)).then(() => notify('Reinstated'))}
                >
                  Reinstate
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {tab === 'support' && (
        <ul className="admin-list">
          {tickets.map((t) => (
            <li key={String(t.id)} className="admin-case">
              <strong>{String(t.subject ?? t.kind)}</strong>
              <p>{String(t.body).slice(0, 200)}</p>
              <div className="modal-actions">
                <button
                  className="button primary"
                  onClick={() =>
                    void client
                      .adminSupportUpdate(String(t.id), { status: 'resolved', reply: 'Thanks — resolved.' })
                      .then(() => load('support'))
                  }
                >
                  Resolve
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {tab === 'audit' && (
        <ul className="admin-list compact">
          {logs.map((l) => (
            <li key={String(l.id)}>
              <code>{String(l.action)}</code> · {String(l.target_type)} · {String(l.created_at)}
            </li>
          ))}
        </ul>
      )}

      {tab === 'policy' && (
        <div className="rules-block">
          <p className="page-sub">Policy v{MODERATION_POLICY_VERSION}</p>
          <h3>Automatic rejection</h3>
          <ul>{AUTO_REJECT.map((r) => <li key={r.signal}>{r.label}</li>)}</ul>
          <h3>Automatic review queue</h3>
          <ul>{AUTO_REVIEW.map((r) => <li key={r.signal}>{r.label}</li>)}</ul>
          <h3>Allowed</h3>
          <ul>{ALLOWED_CONTENT.map((x) => <li key={x}>{x}</li>)}</ul>
          <h3>Remove</h3>
          <ul>{REMOVE_CATEGORIES.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      )}
    </div>
  )
}
